import type { EquivalenciasUmDetalle, UmEquivalenciaDto } from '../services/productoService';

export type ContextoUmVenta = 'pedido' | 'factura' | 'base';

const normUm = (um: string | null | undefined): string => String(um ?? '').trim();

const umKey = (um: string): string => normUm(um).toUpperCase();

/** Lee propiedad string ignorando mayúsculas/minúsculas. */
function readUmProp(obj: Record<string, unknown>, ...candidates: string[]): string {
  const lowerToReal = new Map<string, string>();
  for (const k of Object.keys(obj)) {
    lowerToReal.set(k.toLowerCase(), k);
  }
  for (const c of candidates) {
    const real = lowerToReal.get(c.toLowerCase());
    if (!real) continue;
    const raw = obj[real];
    if (raw == null) continue;
    if (typeof raw === 'object') {
      const nested = raw as Record<string, unknown>;
      const id =
        normUm(nested.idUnidadMedida as string) ||
        normUm(nested.IdUnidadMedida as string) ||
        normUm(nested.codigo as string) ||
        normUm(nested.descripcion as string);
      if (id) return id;
      continue;
    }
    const v = normUm(raw as string);
    if (v) return v;
  }
  return '';
}

/** UM base del artículo (tab Stock). */
export function umBaseProducto(producto: unknown): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  return (
    readUmProp(p, 'idUnidadMedida', 'IdUnidadMedida') ||
    readUmProp(p, 'um', 'Um', 'UM') ||
    readUmProp(p, 'unidadMedida', 'UnidadMedida')
  );
}

/**
 * UM del documento según Logística:
 * - pedido → umrefPed; si falta, UM comercial (factura) antes que stock/base
 *   (si stock es UNI y factura PAQ, el pedido debe poder ir en PAQ)
 * - factura → umrefFac (fallback stock / base)
 */
export function umParaDocumento(producto: unknown, contexto: ContextoUmVenta): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  const base = umBaseProducto(producto);
  if (contexto === 'pedido') {
    return (
      readUmProp(
        p,
        'umrefPed',
        'UmrefPed',
        'umRefPed',
        'UmRefPed',
        'umrefPedido',
        'UmrefPedido',
        'umRefPedido',
      ) ||
      // Comercial antes que stock: evita quedar en UNI cuando solo cargaron PAQ en factura
      readUmProp(p, 'umrefFac', 'UmrefFac', 'umRefFac', 'UmRefFac', 'umrefFactura') ||
      readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock', 'UmRefStock') ||
      base
    );
  }
  if (contexto === 'factura') {
    return (
      readUmProp(
        p,
        'umrefFac',
        'UmrefFac',
        'umRefFac',
        'UmRefFac',
        'umrefFactura',
        'UmrefFactura',
      ) ||
      readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock') ||
      base
    );
  }
  return base;
}

type Edge = { to: string; factor: number };

function buildGraph(eqs: UmEquivalenciaDto[]): Map<string, Edge[]> {
  const g = new Map<string, Edge[]>();
  const add = (from: string, to: string, factor: number) => {
    if (!from || !to || !(factor > 0) || !Number.isFinite(factor)) return;
    const list = g.get(from) ?? [];
    list.push({ to, factor });
    g.set(from, list);
  };
  for (const e of eqs) {
    const o = umKey(e.umOrig);
    const d = umKey(e.umDest);
    const n = Number(e.cantDest);
    if (!o || !d || !(n > 0)) continue;
    // 1 orig = n dest
    add(o, d, n);
    add(d, o, 1 / n);
  }
  return g;
}

/**
 * Factor para convertir cantidad: qtyTo = qtyFrom * factor.
 * Convención equivalencias: 1 UM origen = cantDest UM destino.
 * Si no hay camino, null.
 */
export function factorCantidadEntreUm(
  umFrom: string,
  umTo: string,
  eqs: UmEquivalenciaDto[],
  umBase?: string,
): number | null {
  const a = umKey(umFrom);
  const b = umKey(umTo);
  if (!a || !b) return null;
  if (a === b) return 1;

  const graph = buildGraph(eqs);
  const base = umBase ? umKey(umBase) : '';

  const bfs = (start: string, goal: string): number | null => {
    if (start === goal) return 1;
    const visited = new Set<string>([start]);
    const queue: Array<{ um: string; factor: number }> = [{ um: start, factor: 1 }];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const edge of graph.get(cur.um) ?? []) {
        if (visited.has(edge.to)) continue;
        const nextFactor = cur.factor * edge.factor;
        if (edge.to === goal) return nextFactor;
        visited.add(edge.to);
        queue.push({ um: edge.to, factor: nextFactor });
      }
    }
    return null;
  };

  const direct = bfs(a, b);
  if (direct != null) return direct;

  // Puente por UM base si no hay arista directa en el grafo de equivalencias
  if (base && base !== a && base !== b) {
    const aToBase = bfs(a, base);
    const baseToB = bfs(base, b);
    if (aToBase != null && baseToB != null) return aToBase * baseToB;
  }

  return null;
}

/**
 * Precio de lista se asume por 1 UM base.
 * Devuelve el precio de 1 unidad de `umDocumento`.
 */
export function precioEnUmDocumento(
  precioBase: number,
  umDocumento: string,
  umBase: string,
  eqs: UmEquivalenciaDto[],
): number {
  const basePrice = Number(precioBase) || 0;
  const umDoc = normUm(umDocumento);
  const umB = normUm(umBase) || umDoc;
  if (!umDoc || !umB || umKey(umDoc) === umKey(umB)) return basePrice;

  // Cuántas UM base equivalen a 1 UM documento
  const factor = factorCantidadEntreUm(umDoc, umB, eqs, umB);
  if (factor == null || !Number.isFinite(factor)) return basePrice;
  return basePrice * factor;
}

export function convertirCantidadEntreUm(
  cantidad: number,
  umFrom: string,
  umTo: string,
  eqs: UmEquivalenciaDto[],
  umBase?: string,
): number | null {
  const qty = Number(cantidad);
  if (!Number.isFinite(qty)) return null;
  const factor = factorCantidadEntreUm(umFrom, umTo, eqs, umBase);
  if (factor == null) return null;
  return qty * factor;
}

export interface LineaUmPrecioResolucion {
  um: string;
  umBase: string;
  precioUnitario: number;
  /** true si se aplicó factor de equivalencia distinto de 1 */
  usoEquivalencia: boolean;
}

/**
 * Solo si la UM documental está vacía y hay equivalencias, elige la UM no-base
 * (ej. base UNI + eq 1 KG=10 UNI → KG). No pisa umrefPed/umrefFac explícitos.
 */
export function umDesdeEquivalenciasSiCorresponde(
  umActual: string,
  umBase: string,
  eqs: UmEquivalenciaDto[],
): string {
  const actual = normUm(umActual);
  if (actual) return actual;

  const base = normUm(umBase);
  for (const e of eqs) {
    const o = normUm(e.umOrig);
    const d = normUm(e.umDest);
    if (!o || !d || umKey(o) === umKey(d)) continue;
    if (base) {
      if (umKey(o) === umKey(base)) return d;
      if (umKey(d) === umKey(base)) return o;
    } else {
      return o;
    }
  }
  return base;
}

/** UM comercial explícita en logística (pedido/factura), sin caer a stock/base. */
function umComercialExplicita(producto: unknown, contexto: ContextoUmVenta): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  if (contexto === 'pedido') {
    // Solo umrefPed: no pisar con umrefFac (si Fac=PAQ y el pedido va en KG por equivalencia).
    return readUmProp(
      p,
      'umrefPed',
      'UmrefPed',
      'umRefPed',
      'UmRefPed',
      'umrefPedido',
      'UmrefPedido',
      'umRefPedido',
    );
  }
  if (contexto === 'factura') {
    return readUmProp(
      p,
      'umrefFac',
      'UmrefFac',
      'umRefFac',
      'UmRefFac',
      'umrefFactura',
      'UmrefFactura',
    );
  }
  return '';
}

/** Resuelve UM de documento + precio unitario en esa UM a partir del precio base. */
export function resolverUmYPrecioDocumento(
  producto: unknown,
  precioBase: number,
  contexto: ContextoUmVenta,
  eqs: UmEquivalenciaDto[],
): LineaUmPrecioResolucion {
  const umBase = umBaseProducto(producto);
  // Pedido: umrefPed → equivalencias → umrefFac → stock/base
  // Factura: umrefFac → equivalencias → stock/base
  let um = umComercialExplicita(producto, contexto);
  if (!um) um = umDesdeEquivalenciasSiCorresponde('', umBase, eqs);
  if (!um && contexto === 'pedido') {
    um = readUmProp(
      (producto ?? {}) as Record<string, unknown>,
      'umrefFac',
      'UmrefFac',
      'umRefFac',
      'UmRefFac',
      'umrefFactura',
    );
  }
  if (!um) um = umParaDocumento(producto, contexto) || umBase;
  const precioUnitario = precioEnUmDocumento(precioBase, um, umBase, eqs);
  const factor = factorCantidadEntreUm(um, umBase, eqs, umBase);
  return {
    um,
    umBase,
    precioUnitario,
    usoEquivalencia: factor != null && Math.abs(factor - 1) > 1e-9,
  };
}

/** Incorpora UM de logística (pedido/factura/…) al producto para resolver la línea. */
export function aplicarUmLogisticaProducto(
  producto: unknown,
  detalle:
    | Pick<EquivalenciasUmDetalle, 'umrefPed' | 'umrefFac' | 'umrefPrep' | 'umrefStock' | 'umBase'>
    | null
    | undefined,
): Record<string, unknown> {
  const p = { ...((producto ?? {}) as Record<string, unknown>) };
  if (!detalle) return p;
  if (detalle.umrefPed) p.umrefPed = detalle.umrefPed;
  if (detalle.umrefFac) p.umrefFac = detalle.umrefFac;
  if (detalle.umrefPrep) p.umrefPrep = detalle.umrefPrep;
  if (detalle.umrefStock) p.umrefStock = detalle.umrefStock;
  if (detalle.umBase) {
    p.idUnidadMedida = detalle.umBase;
    if (!normUm(p.um as string)) p.um = detalle.umBase;
  }
  return p;
}

/**
 * Misma resolución que resolverUmYPrecioDocumento, usando el detalle de
 * equivalencias-um (equivalencias + tipo UM de logística).
 */
export function resolverUmYPrecioDesdeDetalle(
  producto: unknown,
  precioBase: number,
  contexto: ContextoUmVenta,
  detalle: EquivalenciasUmDetalle,
): LineaUmPrecioResolucion {
  const productoUm = aplicarUmLogisticaProducto(producto, detalle);
  return resolverUmYPrecioDocumento(
    productoUm,
    precioBase,
    contexto,
    detalle.equivalencias ?? [],
  );
}

/** UM de inventario físico: preferir UM base (ahí suele estar el stock del depósito). */
export function umStockDesdeDetalle(
  detalle: Pick<EquivalenciasUmDetalle, 'umrefStock' | 'umBase'> | null | undefined,
  producto?: unknown,
): string {
  const base =
    normUm(detalle?.umBase) || (producto ? umBaseProducto(producto) : '');
  if (base) return base;
  const refStock = normUm(detalle?.umrefStock);
  if (refStock) return refStock;
  if (!producto) return '';
  const p = producto as Record<string, unknown>;
  return readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock');
}

/**
 * Cantidad de la línea → cantidad en UM de stock.
 * Sin camino de equivalencia: devuelve la misma cantidad (comportamiento normal).
 */
export function cantidadHaciaUmStock(
  cantidad: number,
  umLinea: string,
  umStock: string,
  eqs: UmEquivalenciaDto[],
): number {
  const qty = Number(cantidad);
  if (!Number.isFinite(qty)) return 0;
  const from = normUm(umLinea);
  const to = normUm(umStock) || from;
  if (!from || !to || umKey(from) === umKey(to)) return qty;
  const conv = convertirCantidadEntreUm(qty, from, to, eqs, to);
  return conv == null ? qty : conv;
}

/**
 * Stock en UM de inventario → stock expresado en UM de la línea (para mostrar / comparar).
 * Sin conversión: devuelve el stock tal cual.
 */
export function stockEnUmDocumento(
  stockEnUmStock: number,
  umLinea: string,
  umStock: string,
  eqs: UmEquivalenciaDto[],
): number {
  const stock = Number(stockEnUmStock);
  if (!Number.isFinite(stock)) return stockEnUmStock;
  const to = normUm(umLinea);
  const from = normUm(umStock) || to;
  if (!from || !to || umKey(from) === umKey(to)) return stock;
  const conv = convertirCantidadEntreUm(stock, from, to, eqs, from);
  return conv == null ? stock : conv;
}

/**
 * Intenta expresar el stock del depósito en la UM de la línea.
 * Usa UM base como origen; si no hay camino, prueba umrefStock y cualquier
 * destino/origen de las equivalencias ligado a la UM de línea.
 */
export function resolverStockParaUmLinea(
  stockRaw: number,
  umLinea: string,
  detalle: EquivalenciasUmDetalle | null | undefined,
  producto?: unknown,
): { stock: number; convirtio: boolean; umStockUsada: string } {
  const umDoc = normUm(umLinea);
  const eqs = detalle?.equivalencias ?? [];
  const stock = Number(stockRaw);
  if (!umDoc || !Number.isFinite(stock)) {
    return { stock: stockRaw, convirtio: false, umStockUsada: '' };
  }

  const candidatos: string[] = [];
  const push = (u: string) => {
    const n = normUm(u);
    if (n && !candidatos.some((c) => umKey(c) === umKey(n))) candidatos.push(n);
  };
  push(umStockDesdeDetalle(detalle, producto));
  push(detalle?.umBase || '');
  push(detalle?.umrefStock || '');
  if (producto) push(umBaseProducto(producto));
  for (const e of eqs) {
    push(e.umOrig);
    push(e.umDest);
  }

  for (const umStock of candidatos) {
    if (umKey(umStock) === umKey(umDoc)) continue;
    const conv = convertirCantidadEntreUm(stock, umStock, umDoc, eqs, umStock);
    if (conv != null && Number.isFinite(conv)) {
      return { stock: conv, convirtio: true, umStockUsada: umStock };
    }
  }

  return { stock, convirtio: false, umStockUsada: candidatos[0] || umDoc };
}
